import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { Calendar } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import {
    getExperiment,
    isStatsigInitialized,
    logEvent,
} from '@eventbrite/statsig';
import classNames from 'classnames';
import { css, cx } from 'emotion';
import React, { useEffect } from 'react';
import type { Event } from '../../types/index';
import { UrgencyMessages } from '../UrgencySignals';
import './LegacyConversionBar.scss';

export type LegacyConversionBarCommonProps = {
    hasCroppedLogo: boolean;
    isShareable: boolean;
    isEnded: boolean;
    statusIsUnavailable: boolean;
    shouldDisplayRsvp: boolean;
    shouldShowStatus: boolean;
    panelDisplayPrice: string;
    statusToDisplay: string;
    shouldShowCheckoutButton: boolean;
    getSeriesDefaultOptionTerminology: string;
    ticketModalButton: any;
    shouldShowRemindMeButton: boolean;
    shouldShowTurnOffReminderButton: boolean;
};

export type LegacyConversionBarProps = LegacyConversionBarCommonProps & {
    externalTicketsUrl: string;
    externalTicketsButtonText: string;
    externalTicketsAreAvailable: boolean;
};

export type LegacyConversionBarComponentProps =
    LegacyConversionBarCommonProps & {
        externalTickets?: {
            //TODO: build on the backend
            url: string;
            text: string;
            available: boolean;
        };
    } & Pick<Event, 'id' | 'childEvents' | 'isSeriesParent'>;

export const LegacyConversionBar = ({
    isShareable,
    isEnded,
    statusIsUnavailable,
    shouldDisplayRsvp,
    shouldShowStatus,
    statusToDisplay,
    panelDisplayPrice,
    externalTickets,
    ticketModalButton,
    id, //TODO: rename on picking
    shouldShowRemindMeButton,
    shouldShowCheckoutButton,
    getSeriesDefaultOptionTerminology,
    isSeriesParent,
    hasCroppedLogo,
}: LegacyConversionBarComponentProps) => {
    const noImage = hasCroppedLogo ? '' : 'listing-panel--no-image';

    const canBeShared = isShareable && !isEnded; //TODO: receive just the final one

    const ExternalTickets = () => (
        <a
            href={externalTickets?.url}
            className="eds-btn eds-btn--button eds-btn--fill eds-btn--responsive"
            target="_blank"
            rel="noreferrer"
        >
            {externalTickets?.text}
        </a>
    );

    const StatusUnavailable = () => (
        <span
            data-testid="status-unavailable"
            className="micro-ticket-box__status l-pad-hor-2 hide-small hide-medium"
        >
            {statusToDisplay}
        </span>
    );

    const CheckoutArea = () => (
        <>
            {shouldShowRemindMeButton && (
                <div id="listings-root__remind-me"></div>
            )}
            <CheckoutButton />
        </>
    );

    const CheckoutButton: React.FC = ({ children }) => {
        if (!isStatsigInitialized()) {
            return (
                <div
                    className="conversion-bar-cta__loading"
                    id={`eventbrite-widget-modal-trigger-${id}`}
                ></div>
            );
        }

        return (
            <Button
                id={`eventbrite-widget-modal-trigger-${id}`}
                style={shouldShowStatus ? 'neutral' : 'fill'}
                data-tracking-label={ticketModalButton.tracking_label}
                __containerClassName={classNames({
                    'js-embed-ticket-modal-btn': !isSeriesParent,
                })}
                onClick={() =>
                    logEvent('Listing - conversionBar - Click - CTA')
                }
            >
                {children ? children : ticketModalButton.text}
            </Button>
        );
    };

    const SeriesParent = () => (
        <>
            {shouldShowCheckoutButton ? (
                <>
                    {shouldShowStatus ? (
                        <CheckoutButton />
                    ) : (
                        <CheckoutButton>
                            <Icon type={<Calendar />} />
                            {getSeriesDefaultOptionTerminology}
                        </CheckoutButton>
                    )}
                </>
            ) : (
                <span className="micro-ticket-box__status eds-l-pad-hor-1 hide-small hide-medium">
                    {statusToDisplay}
                </span>
            )}
        </>
    );

    const SingleEvent = () => (
        <>
            {shouldDisplayRsvp ? (
                <div id="listings-root__rsvp"></div>
            ) : (
                <>
                    {statusIsUnavailable && <StatusUnavailable />}

                    {shouldShowCheckoutButton && <CheckoutArea />}

                    {externalTickets?.available && <ExternalTickets />}
                </>
            )}
        </>
    );

    const DisplayStatusOrPrice = () =>
        shouldShowStatus ? (
            <div className="listing-panel-info__sm-text-center">
                {statusToDisplay}
            </div>
        ) : (
            <div className="hide-large listing-panel-info__display-price is-truncated js-panel-display-price-container">
                <div className="js-panel-display-price">
                    {panelDisplayPrice}
                </div>
            </div>
        );

    const ActionsAndDetailPanel = () => (
        <div className="g-cell g-cell-1-1 g-cell-md-4-12 g-cell-lg-3-12 hide-medium hide-small listing-panel__g-cell overflow-visible">
            <h2 className="is-hidden-accessible hide-small">
                {gettext('Actions and Detail Panel')}
            </h2>
            <ul className="listing-panel-actions flex" role="list">
                {canBeShared && (
                    <li
                        className="fx--fade-in-down fx--duration-1 fx--delay-12 z-1"
                        role="listitem"
                        id="listings-root__share-event-modal"
                    ></li>
                )}
                <li
                    className="fx--fade-in-down fx--duration-2 fx--delay-14 z-1"
                    id="listings-root__bookmark-desktop"
                ></li>
            </ul>
        </div>
    );

    const PanelInfo = () =>
        shouldDisplayRsvp ? (
            <div id="listings-root__rsvp-sticky-label"></div>
        ) : (
            <DisplayStatusOrPrice />
        );

    const [isExperimentEnabled, setIsExperimentEnabled] = React.useState(false);

    useEffect(() => {
        const value = getExperiment(
            'urgency_signals_in_listing_page',
            'is_experiment_enabled',
            false,
        );

        setIsExperimentEnabled(value);
    }, []);

    return (
        <div id="conversion-bar">
            <div className="listing-panel-wrapper js-listing-panel-wrapper clrfix">
                <div className="listing-panel clrfix sticky-listing-panel-placeholder hide-medium hide-small"></div>
                <div
                    className={`listing-panel js-sticky-panel sticky-listing-panel sticky clrfix ${noImage}`}
                >
                    <div className="g-group g-group--page-margin-reset listing-panel-info__buttons-and-status">
                        <ActionsAndDetailPanel />
                        <div className="g-cell g-cell-1-1 g-cell-lg-5-12 listing-panel__g-cell">
                            <div
                                className={cx(
                                    styles.panelInfo,
                                    'l-pad-vert-1 l-lg-pad-vert-0',
                                )}
                            >
                                {isExperimentEnabled && (
                                    <>
                                        <div className="hide-small hide-medium listing-panel-info__urgency-signal">
                                            <UrgencyMessages />
                                        </div>
                                    </>
                                )}
                                {!statusIsUnavailable && (
                                    <div className="listing-panel-info__price-or-status">
                                        <PanelInfo />
                                        {/**..... */}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="conversion-bar-cta eds-fx--fade-in eds-fx--delay-18">
                            {isSeriesParent ? (
                                <SeriesParent />
                            ) : (
                                <SingleEvent />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    panelInfo: css`
        display: flex;
        flex-direction: row;
        justify-content: center;
        column-gap: 16px;

        @media (min-width: 800px) {
            justify-content: right;
        }
    `,
};
